import { useEffect, useRef, useState } from 'react'
import { AnimatePresence, motion } from 'framer-motion'

import { useSession } from '../../../contexts/SessionContext'
import { useDirectDebitStatus, DirectDebitStatus } from '../../../contexts/session/DirectDebitContext'
import { SessionStatus } from '../../../constants/SessionConstants'
import {
  useReducedMotionPreference,
  DURATION,
  EASE_IN_OUT,
  EASE_ENTRANCE,
  SCREEN_SLIDE_DISTANCE,
} from '../../../styles/motion'

import Header from './Header'
import Footer from './Footer'
import EntryScreen from '../../pages/payments/direct-debit/new-user/EntryScreen'
import YourDetailsScreen from '../../pages/payments/direct-debit/new-user/YourDetailsScreen'
import OneTimeTransferScreen from '../../pages/payments/direct-debit/new-user/OneTimeTransferScreen'
import MandateCreatedScreen from '../../pages/payments/direct-debit/new-user/MandateCreatedScreen'
import LinkExpiredScreen from '../../pages/payments/direct-debit/shared/LinkExpiredScreen'
import SomethingWentWrongScreen from '../../pages/payments/direct-debit/shared/SomethingWentWrongScreen'
import HowItWorksSheet from '../../pages/payments/direct-debit/sheets/HowItWorksSheet'
import EnterCodeSheet from '../../pages/payments/direct-debit/sheets/EnterCodeSheet'
import WhyFiftyNairaSheet from '../../pages/payments/direct-debit/sheets/WhyFiftyNairaSheet'

import styles from './Container.module.css'

const SCREEN_ORDER = [
  DirectDebitStatus.ENTRY,
  DirectDebitStatus.YOUR_DETAILS,
  DirectDebitStatus.ONE_TIME_TRANSFER,
  DirectDebitStatus.MANDATE_CREATED,
]

// Spec §8 — screen forward/back slide direction is derived from each status's position in
// SCREEN_ORDER. Crossing into/out of a terminal screen always reads as forward since
// there's no meaningful "back" relationship there.
const getSlideDirection = (prevStatus, nextStatus) => {
  const prevIndex = SCREEN_ORDER.indexOf(prevStatus)
  const nextIndex = SCREEN_ORDER.indexOf(nextStatus)
  if (prevIndex === -1 || nextIndex === -1) return 1
  return nextIndex >= prevIndex ? 1 : -1
}

const SCREEN_COMPONENTS = {
  [DirectDebitStatus.ENTRY]: EntryScreen,
  [DirectDebitStatus.YOUR_DETAILS]: YourDetailsScreen,
  [DirectDebitStatus.ONE_TIME_TRANSFER]: OneTimeTransferScreen,
  [DirectDebitStatus.MANDATE_CREATED]: MandateCreatedScreen,
  [DirectDebitStatus.EXPIRED]: LinkExpiredScreen,
  [DirectDebitStatus.FAILED]: SomethingWentWrongScreen,
  [DirectDebitStatus.ERROR]: SomethingWentWrongScreen,
}

/**
 * Owns the widget's 442x688 frame (full-screen on mobile), the merchant-close chrome,
 * and the AnimatePresence screen router that replaces the old DirectDebitPayment.js
 * switch. Also owns the SessionStatus -> DirectDebitStatus mapping effect (ported and
 * adapted from DirectDebitPayment.js) and renders whichever sheet is active.
 *
 * NOTE: only the new-user flow is wired up - the spec's returning-user flow (R1-R4) isn't
 * built since the backend has no way to recognise a returning customer today. See final report.
 */
const Container = () => {
  const { sessionId: sessionContextId, sessionData, stopSessionTimer } = useSession()
  const {
    directDebitStatus,
    activeSheet,
    closeSheet,
    setSessionKey,
    setSessionId,
    showEntry,
    showOneTimeTransfer,
    showMandateCreated,
    showFailed,
    showExpired,
  } = useDirectDebitStatus()

  const reduceMotion = useReducedMotionPreference()
  const [direction, setDirection] = useState(1)
  const prevStatusRef = useRef(directDebitStatus.status)
  const lastMappedStatusRef = useRef(null)

  useEffect(() => {
    if (sessionContextId) {
      setSessionKey(sessionContextId)
      setSessionId(sessionContextId)
    }
  }, [sessionContextId, setSessionKey, setSessionId])

  useEffect(() => {
    if (directDebitStatus.status === prevStatusRef.current) return
    setDirection(getSlideDirection(prevStatusRef.current, directDebitStatus.status))
    prevStatusRef.current = directDebitStatus.status
  }, [directDebitStatus.status])

  // Ported from DirectDebitPayment.js's session->status effect, adapted for the new
  // screen set. VERIFICATION_PENDING now opens the OTP sheet in place rather than doing a
  // full top-level transition (spec §4 state-machine change).
  useEffect(() => {
    if (!sessionData?.status || sessionData.status === lastMappedStatusRef.current) return
    lastMappedStatusRef.current = sessionData.status

    switch (sessionData.status) {
      case SessionStatus.INITIALIZED:
      case SessionStatus.PENDING:
        showEntry()
        break
      case SessionStatus.VERIFICATION_PENDING:
      case SessionStatus.OTP_VERIFICATION:
        // handled by YourDetailsScreen/EnterCodeSheet, which progress the flow themselves
        break
      case SessionStatus.CONFIRMATION:
        if (directDebitStatus.status !== DirectDebitStatus.ONE_TIME_TRANSFER) {
          showOneTimeTransfer()
        }
        break
      case SessionStatus.CONFIRMED:
      case SessionStatus.SUCCESS:
        showMandateCreated()
        break
      case SessionStatus.FAILED:
      case SessionStatus.CANCELLED:
        showFailed(sessionData.message || '')
        break
      case SessionStatus.EXPIRED:
        stopSessionTimer()
        showExpired()
        break
      default:
        showEntry()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionData])

  const ScreenComponent = SCREEN_COMPONENTS[directDebitStatus.status] ?? EntryScreen

  const screenVariants = {
    enter: (dir) => ({ opacity: 0, x: dir * SCREEN_SLIDE_DISTANCE }),
    center: { opacity: 1, x: 0 },
    exit: (dir) => ({ opacity: 0, x: -dir * SCREEN_SLIDE_DISTANCE }),
  }

  return (
    <div className={styles.scrim}>
      <motion.div
        className={`${styles.widget} dd-widget`}
        initial={reduceMotion ? false : { opacity: 0, y: 18, scale: 0.985 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ duration: DURATION.widgetEntrance, ease: EASE_ENTRANCE }}
      >
        <Header />
        <div className={styles.body}>
          <AnimatePresence mode='wait' custom={direction} initial={false}>
            <motion.div
              key={directDebitStatus.status}
              custom={direction}
              variants={reduceMotion ? undefined : screenVariants}
              initial='enter'
              animate='center'
              exit='exit'
              transition={{ duration: DURATION.screenSlide, ease: EASE_IN_OUT }}
              style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}
            >
              <ScreenComponent />
            </motion.div>
          </AnimatePresence>
        </div>
        <Footer />
        <EnterCodeSheet isOpen={activeSheet === 'enter-code'} onClose={closeSheet} />
        <HowItWorksSheet isOpen={activeSheet === 'how-it-works'} onClose={closeSheet} />
        <WhyFiftyNairaSheet isOpen={activeSheet === 'why-fifty'} onClose={closeSheet} />
      </motion.div>
    </div>
  )
}

export default Container
